import CharacterCreator from './model/character/CharacterCreator';
import Totals from './model/crew/Totals';
import LayeredTerrainGenerator from './model/generator/LayeredTerrainGenerator';
import GridLoadingManager from './model/grid/GridLoadingManager';
import WorldGrid from './model/grid/WorldGrid';
import LightingCalculator from './model/lighting/LightingCalculator';
import PathPlanFinder from './model/path/PathPlanFinder';
import PhysicsManager from './model/physics/PhysicsManager';
import GridSave from './model/save/GridSave';
import SaveManager from './model/save/SaveManager';
import Sprites from './model/sprite/Sprites';
import Projection from './Projection';
import { Settings } from './Settings';
import EffectManager from './model/effect/EffectManager';
import SoundEffectManager from './model/sound/SoundEffectManager';
import EffectCreators from './model/effect/EffectCreators';
import LightSourceCreators from './model/lighting/LightSourceCreators';
import CharacterBrains from './model/character/brain/CharacterBrains';
import InfoTextProcessor from './model/info/InfoTextProcessor';
import GameTime from './model/time/GameTime';
import UpgradeCollection from './model/upgrade/UpgradeCollection';
import Values from './model/value/Values';
import UpgradeCreators from './model/upgrade/free/UpgradeCreators';
import TileMinedLogic from './logic/TileMinedLogic';
import VectorFieldGenerator from './model/path/VectorFieldGenerator';
import VectorField from './model/grid/VectorField';
import MineDamageLogic from './logic/MineDamageLogic';
import PanAnimation from './logic/PanAnimation';
import GridLoader from './model/grid/GridLoader';
import PartyTarget from './model/character/PartyTarget';
import Crew from './model/crew/Crew';
import GridSaveManager from './model/save/GridSaveManager';
import PickUpgradeCollection from './model/upgrade/ore/PickUpgradeCollection';
import CrewBrain from './model/crew/CrewBrain';
import Statistics from './model/statistics/Statistics';
import SkillTreeGraph from './model/skillTree/SkillTreeGraph';
import StatisticsIncrementor from './model/statistics/StatisticsIncrementor';
import GameModel from './GameModel';
import GlobalStatistics from './model/statistics/GlobalStatistics';
import Damage from './model/crew/Damage';
import BonusCollection from './model/bonus/BonusCollection';
import AutomationManager from './model/automation/AutomationManager';
import GoldUpgradeCollection from './model/upgrade/gold/GoldUpgradeCollection';
import OfflineLogic from './logic/OfflineLogic';
import MinedUpgradeCollection from './model/upgrade/free/MinedUpgradeCollection';
import MineControl from './model/control/MineControl';
import OreCalculationLogic from './logic/OreCalculationLogic';
import GameOptions from './model/options/GameOptions';

export default class State extends GameModel {
    /**
     * @param {GameStateControl} gameStateControl 
     */
    constructor(gameStateControl) {
        super();

        /**
         * @constant
         * @type {GameModel[]}
         */
        this.gameModels = [];

        /**
         * @constant
         * @type {Sprites}
         */
        this.sprites = new Sprites();

        /**
         * @constant
         * @type {WorldGrid}
         */
        this.worldGrid = new WorldGrid();
        this.gameModels.push(this.worldGrid);

        /**
         * @constant
         * @type {Projection}
         */
        this.projection = new Projection();
        this.gameModels.push(this.projection);

        /**
         * @constant
         * @type {GameTime}
         */
        this.gameTime = new GameTime();
        this.gameModels.push(this.gameTime);

        /**
         * @constant
         * @type {Statistics}
         */
        this.globalStatistics = new GlobalStatistics();
        this.gameModels.push(this.globalStatistics);

        /**
         * @constant
         * @type {Statistics}
         */
        this.runStatistics = new Statistics();
        this.gameModels.push(this.runStatistics);

        /**
         * @constant
         * @type {GameOptions}
         */
        this.gameOptions = new GameOptions();

        /**
         * @type {StatisticsIncrementor}
         */
        this.statisticsIncrementor = new StatisticsIncrementor(this.globalStatistics, this.runStatistics);
        // nothing to reset

        /**
         * @constant
         * @type {Values}
         */
        this.values = new Values();
        this.gameModels.push(this.values);

        /**
         * @constant
         * @type {Crew}
         */
        this.crew = new Crew(this.worldGrid, this.gameTime, this.sprites.characterSpritesheet, this.sprites.itemOverlaySpritesheet, this.values);
        this.gameModels.push(this.crew);
        
        /**
         * @constant
         * @type {GridLoadingManager}
         */
        this.gridLoadingManager = new GridLoadingManager(this.worldGrid, this.projection);
        this.gameModels.push(this.gridLoadingManager);

        /**
         * @type {boolean}
         */
        this.gameVisible = true;
        /**
         * @type {boolean}
         */
        this.gamePaused = false;
        /**
         * The world seed. The world seed must be in the range of 0-65536 (2^16).
         * @type {number}
         */
        this.worldSeed = 1;
        /**
         * @type {number}
         */
        this.currentFps = 0;

        /**
         * @constant
         * @type {VectorField}
         */
        this.vectorField = new VectorField();
        this.gameModels.push(this.vectorField);

        /**
         * @constant
         * @type {Totals}
         */
        this.totals = new Totals(this.gameTime);
        this.gameModels.push(this.totals);

        /**
         * @constant
         * @type {Damage}
         */
        this.damage = new Damage();
        this.gameModels.push(this.damage);

        /**
         * @constant
         * @type {EffectManager}
         */
        this.effectManager = new EffectManager(this.projection);
        this.gameModels.push(this.effectManager);

        /**
         * @constant
         * @type {LightSourceCreators}
         */
        this.lightSourceCreators = new LightSourceCreators();
        this.gameModels.push(this.lightSourceCreators);

        /**
         * @constant
         * @type {EffectCreators}
         */
        this.effectCreators = new EffectCreators(this.lightSourceCreators, this.sprites.spellFxSpritesheet);
        this.gameModels.push(this.effectCreators);

        /**
         * @constant
         * @type {LightingCalculator}
         */
        this.lightingCalculator = new LightingCalculator(this.worldGrid, this.projection);
        this.gameModels.push(this.lightingCalculator);

        /**
         * @constant
         * @type {PhysicsManager}
         */
        this.physicsManager = new PhysicsManager(this.worldGrid, this.projection, this.gameTime);

        /**
         * @constant
         * @type {SoundEffectManager}
         */
        this.soundEffectManager = new SoundEffectManager();

        /**
         * @constant
         * @type {InfoTextProcessor}
         */
        this.infoTextProcessor = new InfoTextProcessor(this.projection);
        this.gameModels.push(this.infoTextProcessor);

        /**
         * @constant
         * @type {PartyTarget}
         */
        this.partyTarget = new PartyTarget(this.totals, this.damage);
        this.gameModels.push(this.partyTarget);

        /**
         * @constant
         * @type {MineDamageLogic}
         */
        this.mineDamageLogic = new MineDamageLogic(this.values, this.totals, this.damage, this.globalStatistics);

        /**
         * @constant
         * @type {OfflineLogic}
         */
        this.offlineLogic = new OfflineLogic(this.totals, this.values, this.statisticsIncrementor);

        /**
         * @constant
         * @type {UpgradeCollection}
         */
        this.minedUpgradeCollection = new MinedUpgradeCollection();
        this.gameModels.push(this.minedUpgradeCollection);

        /**
         * @constant
         * @type {UpgradeCollection}
         */
        this.pickUpgradeCollection = new PickUpgradeCollection(this.totals, this.values, this.mineDamageLogic);
        this.gameModels.push(this.pickUpgradeCollection);

        /**
         * @constant
         * @type {UpgradeCollection}
         */
        this.goldUpgradeCollection = new GoldUpgradeCollection(this.totals, this.values, this.mineDamageLogic, this.damage);
        this.gameModels.push(this.goldUpgradeCollection);

        /**
         * @constant
         * @type {BonusCollection}
         */
        this.bonusCollection = new BonusCollection(this.gameTime, this.totals, this.values);
        this.gameModels.push(this.bonusCollection);

        /**
         * @constant
         * @type {VectorFieldGenerator}
         */
        this.vectorFieldGenerator = new VectorFieldGenerator(this.worldGrid, this.projection, this.vectorField, this.partyTarget, this.crew);
        this.gameModels.push(this.vectorFieldGenerator);

        /**
         * @constant
         * @type {OreCalculationLogic}
         */
        this.oreCalculationLogic = new OreCalculationLogic(this.values);
        // nothing to reset.

        /**
         * @constant
         * @type {UpgradeCreators}
         */
        this.upgradeCreators = new UpgradeCreators(this.values, this.totals, this.oreCalculationLogic, this.mineDamageLogic, this.minedUpgradeCollection, this.statisticsIncrementor);
        // nothing to reset

        /**
         * @constant
         * @type {TileMinedLogic}
         */
        this.tileMinedLogic = new TileMinedLogic(this.crew, this.totals, this.damage, this.values, this.statisticsIncrementor, this.minedUpgradeCollection, 
            this.upgradeCreators, this.infoTextProcessor, this.effectCreators, this.effectManager, this.oreCalculationLogic, this.mineDamageLogic, this.gameOptions);
        // nothing to reset. 

        /**
         * @constant
         * @type {SkillTreeGraph}
         */
        this.skillTreeGraph = new SkillTreeGraph(this.gameTime, this.worldGrid, this.totals, this.values, this.tileMinedLogic, this.mineDamageLogic,
            this.crew, this.vectorField, this.sprites.spellFxSpritesheet);
        this.gameModels.push(this.skillTreeGraph);

        /**
         * @constant
         * @type {AutomationManager}
         */
        this.automationManager = new AutomationManager(this.minedUpgradeCollection, this.pickUpgradeCollection, gameStateControl, 
            this.values, this.totals, this.gameTime);
        this.gameModels.push(this.automationManager);

        /**
         * @constant
         * @type {PathPlanFinder}
         */
        this.pathPlanFinder = new PathPlanFinder(this.worldGrid, this.vectorField, this.tileMinedLogic);
        this.gameModels.push(this.pathPlanFinder);

        /**
         * @constant
         * @type {CrewBrain}
         */
        this.crewBrain = new CrewBrain(this.crew, this.vectorField, this.partyTarget, this.pathPlanFinder, 
            this.tileMinedLogic, this.mineDamageLogic, this.statisticsIncrementor);
        // nothing to reset

        /**
         * @constant
         * @type {MineControl}
         */
        this.mineControl = new MineControl(this.crew, this.partyTarget, this.damage, this.worldGrid, this.vectorFieldGenerator, this.vectorField);
        this.gameModels.push(this.mineControl);
        
        /**
         * @constant
         * @type {CharacterBrains}
         */            
        this.characterBrains = new CharacterBrains(this.pathPlanFinder);
        // nothing to reset

        /**
         * @constant
         * @type {CharacterCreator}
         */
        this.characterCreator = new CharacterCreator(this.sprites.monsterSpritesheet, this.characterBrains);
        this.gameModels.push(this.characterCreator);

        /**
         * @constant
         * @type {GridSave}
         */
        this.gridSave = new GridSave(this.characterCreator);
        // nothing to reset

        /**
         * @constant
         * @type {GridSaveManager}
         */
        this.gridSaveManager = new GridSaveManager(this.gridSave);
        this.gameModels.push(this.gridSaveManager);

        /**
         * @constant
         * @type {SaveManager}
         */
        this.saveManager = new SaveManager(this.gridSaveManager);
        // nothing to reset

        /**
         * @constant
         * @type {PanAnimation}
         */
        this.panAnimation = new PanAnimation(this.projection, this.crew);
        this.gameModels.push(this.panAnimation);
    }

    /**
     * Called after the save file have been loaded.
     * @param {boolean} saveLoaded True if the save file has been loaded, false if this is a new game with no save data.
     */
    initializeState(saveLoaded) {

        /////////////////////////////////////////
        // Handle World Seed Loaded from Save
        /////////////////////////////////////////

        const terrainGenerator = new LayeredTerrainGenerator(this.worldSeed, this.characterCreator);

        /////////////////////////////////////////
        // Load the world so that it exists for the crew
        /////////////////////////////////////////

        const gridLoader = new GridLoader(this.gridSaveManager, terrainGenerator, this.worldGrid, this.crew, this.vectorField);
        this.gridLoadingManager.initializeLoadingManager(gridLoader, this.crew);

        // Make sure the main character exists. Place them into the world.
        if (!this.crew.main) {
            const mainMiner = this.crew.createMainMiner();

            let tile = null;
            if (saveLoaded) {
                tile = this.worldGrid.findGridTile(mainMiner.position.worldCoordinateOrigin);
            }
            else {
                tile = this.worldGrid.findGridTileXY(Settings.start.startX, Settings.start.startY);
            }

            if (tile) {
                mainMiner.position.setTileAndOriginPosition(tile, tile.origin.x, tile.origin.y);
            }
            else {
                if (!saveLoaded) {
                    mainMiner.position.setWorldCoordinateOriginXY(Settings.start.startX, Settings.start.startY);
                }
            }
        }
        else {
            const tile = this.worldGrid.findGridTile(this.crew.main.position.worldCoordinateOrigin);
            if (tile) {
                this.crew.main.position.setTileAndOriginPosition(tile, tile.origin.x, tile.origin.y);
            }
        }

        /////////////////////////////////////////////////////////////
        // If we loaded the save, then calculate offline earnings
        /////////////////////////////////////////////////////////////

        if (saveLoaded) {
            const currentTime = Date.now();
            const saveTime = this.gameTime.saveTime;
            const offlineMillis = currentTime - saveTime;
            const offlineSeconds = (offlineMillis / 1000) | 0;

            if (offlineSeconds > 0) {
                this.totals.offlineSeconds += offlineSeconds;
                this.offlineLogic.calculateOfflineEarnings();
            }
        }

        // Calculate the character's total mine damage.
        this.mineDamageLogic.calculateTotalMineDamagePerTurn();
    }

    beforeImportSave() {
        for (let i = 0; i < this.gameModels.length; i++) {
            this.gameModels[i].resetFull();
        }
    }

    afterImportSave() {
        this.initializeState(true);

        this.projection.setGridCenter(Settings.start.startX, Settings.start.startY);
        this.gridLoadingManager.manageLoadedGridsForFrame();
    }

    /**
     * @override
     */
    resetForPrestige() {
        /////////////////////////////////////////
        // Reset the game models
        /////////////////////////////////////////

        for (let i = 0; i < this.gameModels.length; i++) {
            this.gameModels[i].resetForPrestige();
        }

        /////////////////////////////////////////
        // Set things up for next run
        /////////////////////////////////////////

        this.worldSeed++;

        this.initializeState(false);

        this.projection.setGridCenter(Settings.start.startX, Settings.start.startY);
        this.gridLoadingManager.manageLoadedGridsForFrame();
    }

    /**
     * @override
     */
    resetFull() {
        /////////////////////////////////////////
        // Reset the game models
        /////////////////////////////////////////

        for (let i = 0; i < this.gameModels.length; i++) {
            this.gameModels[i].resetFull();
        }

        /////////////////////////////////////////
        // Set things up for next run
        /////////////////////////////////////////

        this.worldSeed++;

        // The crew has been cleared out.
        this.initializeState(false);

        this.projection.setGridCenter(Settings.start.startX, Settings.start.startY);
        this.gridLoadingManager.manageLoadedGridsForFrame(); // <-- uses projection.gridCenter
    }
}